import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useToken } from "@packsize/authentication";

import { ProductionGroupProvider } from "@/contexts/production-group";
import { readLocalStorage, removeLocalStorage } from "@/api/local-storage";
import { type MachineGroup } from "@/schemas/machine-group";
import { type ProductionGroup } from "@/schemas/production-group";

import { MACHINE_GROUP_KEY, MachineGroupProvider } from "../machine-group";

import { ProductionContext } from "./production.context";

const getStoredMachineGroup = (): MachineGroup | null => {
	try {
		const storedMachineGroup = readLocalStorage(MACHINE_GROUP_KEY);
		if (storedMachineGroup) {
			return JSON.parse(storedMachineGroup) as MachineGroup;
		}
		return null;
	} catch (e) {
		console.error(e);
		return null;
	}
};

interface ProductionProviderProps {
	readonly children: ReactNode;
}

export const ProductionProvider = ({ children }: ProductionProviderProps) => {
	const [machineGroups, setMachineGroups] = useState<MachineGroup[]>([]);
	const [currentMachineGroup, setCurrentMachineGroup] = useState<MachineGroup | null>(getStoredMachineGroup());
	const [productionGroups, setProductionGroups] = useState<ProductionGroup[]>([]);
	const [currentProductionGroup, setCurrentProductionGroup] = useState<ProductionGroup | null>(null);

	const { decodedToken } = useToken();
	const tenantId = decodedToken?.tid;
	const previousTenant = useRef(tenantId);

	// Clear the selected machine/production group when the user switches tenant,
	useEffect(() => {
		if (previousTenant.current !== undefined && previousTenant.current !== tenantId) {
			setCurrentMachineGroup(null);
			setCurrentProductionGroup(null);
			removeLocalStorage(MACHINE_GROUP_KEY);
		}

		previousTenant.current = tenantId;
	}, [tenantId]);

	const value = useMemo(
		() => ({
			machineGroups,
			currentMachineGroup,
			productionGroups,
			currentProductionGroup,
			setMachineGroups,
			setProductionGroups,
			setCurrentMachineGroup,
			setCurrentProductionGroup,
		}),
		[
			currentMachineGroup,
			currentProductionGroup,
			machineGroups,
			productionGroups,
			setCurrentMachineGroup,
			setCurrentProductionGroup,
			setMachineGroups,
			setProductionGroups,
		],
	);

	return (
		<ProductionContext.Provider value={value}>
			<ProductionGroupProvider>
				<MachineGroupProvider>{children}</MachineGroupProvider>
			</ProductionGroupProvider>
		</ProductionContext.Provider>
	);
};
